import { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { Droppable } from '@hello-pangea/dnd';
import localData from 'utils/localData';
import { IssueStatus, getIssueStatus } from 'shared/constants/kenshin/issues';

import Issue from './Issue';
import { List, Title, IssuesCount, Issues, ExtraItemsBlock } from './Styles';
import { ProjectProptypes } from 'shared/propTypes/projectType';
import { USER_COMMENT } from 'shared/constants/comment';
import Typography from '@mui/material/Typography';
import MDLoadingButton from 'components/MDLoadingButton';

const propTypes = {
  progress: PropTypes.string.isRequired,
  project: PropTypes.shape(ProjectProptypes).isRequired,
  fetchProject: PropTypes.func.isRequired,
  currentUserId: PropTypes.string,
};

const ProjectBoardList = ({
  progress,
  project,
  fetchProject,
  currentUserId = null,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const getMoreIssuesStatusDone = async () => {
    setIsLoading(true);
    fetchProject({
      params: {
        userId: currentUserId,
        organizationId: localData.get(USER_COMMENT.CURRENT_ORGID),
        dateThres: project.dateThres,
        fetchMoreDoneIssues: true,
      },
    });
  };
  // Use useEffect to monitor the project prop and stop loading when it updates
  useEffect(() => {
    // Whenever project updates, set isLoading to false
    setIsLoading(false);
  }, [project]); // The effect runs when the 'project' prop updates

  const getSortedListIssues = (issues, progress) => {
    if (progress === IssueStatus.DONE) {
      return issues
        .filter((issue) => issue.progress === progress)
        .sort((a, b) => a.id - b.id);
    } else {
      return issues
        .filter((issue) => issue.progress === progress)
        .sort(function (a, b) {
          if (a.priority === b.priority) {
            return a.listPosition - b.listPosition;
          }
          return a.priority < b.priority ? 1 : -1;
        });
    }
  };

  const formatIssuesCount = (allListIssues, filteredListIssues) => {
    if (allListIssues.length !== filteredListIssues.length) {
      return `${filteredListIssues.length} of ${allListIssues.length}`;
    }
    return allListIssues.length;
  };

  const filteredListIssues = getSortedListIssues(project.issues, progress);
  const allListIssues = getSortedListIssues(project.issues, progress);

  return (
    <Droppable
      key={progress}
      droppableId={progress}
      data-testid={`board-list-droppable:${progress}`}>
      {(provided) => (
        <List>
          <Title>
            {`${getIssueStatus(progress)} `}
            <IssuesCount>
              {formatIssuesCount(allListIssues, filteredListIssues)}
            </IssuesCount>
          </Title>
          <Issues
            {...provided.droppableProps}
            ref={provided.innerRef}
            data-testid={`board-list:${progress}`}>
            {filteredListIssues.map((issue, index) => (
              <Issue
                key={issue.id}
                projectUsers={project.users}
                issue={issue}
                index={index}
              />
            ))}
            {provided.placeholder}

            {progress === IssueStatus.DONE && (
              <ExtraItemsBlock>
                {project.noMoreDoneIssues ? (
                  <Typography
                    style={{
                      opacity: 0.6,
                      fontSize: '1rem',
                    }}>
                    {intl.get('kanban_done_column_text_loaded_all')}
                  </Typography>
                ) : (
                  <MDLoadingButton
                    circular={true}
                    variant="contained"
                    color="tertiary"
                    loading={isLoading}
                    loadingPosition="start"
                    onClick={getMoreIssuesStatusDone}>
                    {intl.get('kanban_button_load_more')}
                  </MDLoadingButton>
                )}
              </ExtraItemsBlock>
            )}
          </Issues>
        </List>
      )}
    </Droppable>
  );
};

ProjectBoardList.propTypes = propTypes;

export default ProjectBoardList;
